import React, { lazy } from 'react';
import { Box, Flex, Text, Link, Heading } from '@chakra-ui/react';
import { useStaticQuery, graphql } from 'gatsby';
import { Wrapper } from '@components';
import Media from '@components/atoms/Media';
import RichText from '@components/atoms/RichText';
import SuspenseLoader from '@components/atoms/SuspenseLoader';
import SuspenseHelper from '@components/atoms/SuspenseHelper';
import TickerTape from '@components/molecules/TickerTape';
import FooterMenu from '@components/molecules/FooterMenu';
import { useSeoMeta } from '../../hooks/useSeoMeta';

/* Lazy-load share page block to avoid SSR issues */
const SharePageBlock = lazy(
  () => import(`@components/molecules/SharePageBlock`)
  // eslint-disable-next-line function-paren-newline
);

const Footer = ({ location, seo }) => {
  const { siteSettings } = useStaticQuery(graphql`
    {
      siteSettings: strapiSiteSettings {
        copyright
        legal {
          data {
            legal
          }
        }
        socialMediaLinks {
          socialTitle
          socialLinksList {
            name
            url
            icon {
              alternativeText
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    formats: [AUTO, WEBP, AVIF]
                    placeholder: BLURRED
                    quality: 70
                    height: 24
                  )
                }
              }
            }
          }
        }
        tickerTape {
          tickerTapeImages {
            images {
              alternativeText
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 70
                    height: 10
                    layout: FIXED
                    placeholder: NONE
                  )
                }
              }
            }
          }
        }
      }
    }
  `);

  const copyright = siteSettings.copyright || ``;
  const legal = siteSettings.legal.data.legal || ``;
  const socialTitle = siteSettings.socialMediaLinks.socialTitle || ``;
  const socialLinksList = siteSettings.socialMediaLinks.socialLinksList || ``;
  const tickerTape = siteSettings.tickerTape.tickerTapeImages || ``;

  const {
    site: {
      siteMetadata: { language }
    }
  } = useSeoMeta();

  return (
    <>
      <SuspenseHelper fallback={<SuspenseLoader />}>
        <SharePageBlock seo={seo} location={location} />
      </SuspenseHelper>
      <Box as="footer" bg="purple.600" color="white" padding="2.5rem 0 1.5rem">
        <Wrapper>
          <Flex flexDirection="column">
            <Flex
              flexDirection={{ base: `column-reverse`, md: `row` }}
              paddingBottom="1rem"
              alignItems="flex-start">
              <FooterMenu />
              <Box
                paddingBottom={{ base: `2rem`, md: `0` }}
                width={{ base: `100%`, md: `33%` }}>
                <Heading
                  as="h4"
                  size="sm"
                  paddingBottom="1.2rem"
                  textAlign={{ base: `center`, md: `left` }}
                  fontWeight="600"
                  fontStyle="italic">
                  {socialTitle}
                </Heading>
                <Flex justifyContent={{ base: `center`, md: `flex-start` }}>
                  {socialLinksList.map((item, i) => (
                    <Box
                      key={`socialLinksList${i + 1}`}
                      margin="0 1rem"
                      _first={{
                        marginLeft: { md: `0` }
                      }}>
                      <Link href={item.url} hrefLang={language.toLowerCase()}>
                        <Media
                          width="auto"
                          height="24px"
                          objectFit="cover"
                          display="inline-flex"
                          media={item.icon}
                        />
                      </Link>
                    </Box>
                  ))}
                </Flex>
              </Box>
            </Flex>
            <Box
              fontSize="xs"
              padding="1.5rem 0 0.5rem"
              borderTop="1px"
              borderBottom="1px"
              borderColor="whiteAlpha.500">
              <RichText data={legal} />
            </Box>
            <Text
              fontSize="xs"
              marginBottom="0"
              marginTop="1.5rem"
              textAlign="center">
              {copyright}
            </Text>
          </Flex>
        </Wrapper>
      </Box>
      <TickerTape tickerTapeItems={tickerTape} bg="purple.400" height="24px" />
    </>
  );
};

export default Footer;
